import React, { useCallback, useEffect } from "react";
import { useAuth } from "../auth/AuthContext";
import { useIsAuthenticated } from "@azure/msal-react";
import {
  buildAuthorizeUrl,
  augmentAuthorizeUrlWithPkce,
  readEnv,
} from "../auth/oauth";
import { storage } from "../auth/storage";
import {
  isAzureLoginEnabled,
  isSfLoginEnabled,
  getMsalInstance,
} from "../auth/azureConfig";
import { getEnv } from "../config/getEnv";
import { Button, Spin } from "antd";
import { useLocation, useNavigate } from "react-router-dom";

function clearUrlError() {
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.has("error")) {
      url.searchParams.delete("error");
      const next =
        url.pathname +
        (url.searchParams.toString() ? `?${url.searchParams.toString()}` : "") +
        url.hash;
      window.history.replaceState({}, document.title, next);
    }
  } catch {}
}

function clearAuthStorage() {
  try {
    storage.clearTemp();
    storage.clearAllAuthSessions();
  } catch {}
}

function LoginAzureFlow() {
  const azureSignedIn = useIsAuthenticated();
  const { login: loginAzure, isUserLoading, isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const azureSessionActive = azureSignedIn || storage.hasAzureSession();
  const sfSessionActive = storage.hasSfSession();

  useEffect(() => {
    if (!isUserLoading && isAuthenticated) {
      const from = location.state?.from?.pathname ?? "/";
      navigate(from, { replace: true });
    }
  }, [
    isUserLoading,
    isAuthenticated,
    navigate,
    location.state?.from?.pathname,
  ]);

  const handleMicrosoftLogin = useCallback(() => {
    clearUrlError();
    clearAuthStorage();
    loginAzure(true);
  }, [loginAzure]);

  useEffect(() => {
    const btn = document.getElementById("login-btn");
    btn?.focus();
    if (
      getEnv("REACT_APP_AUTO_LOGIN") === "true" &&
      !isAuthenticated &&
      !isUserLoading &&
      (!azureSessionActive || !sfSessionActive)
    ) {
      const timeout = setTimeout(() => {
        if (!azureSessionActive) {
          handleMicrosoftLogin();
        } else if (!sfSessionActive) {
          handleSalesforceLogin();
        }
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [
    isUserLoading,
    azureSessionActive,
    sfSessionActive,
    handleMicrosoftLogin,
    isAuthenticated,
  ]);

  useEffect(() => {
    if (!isUserLoading) return;
    clearUrlError();
  }, [isUserLoading, location.pathname]);

  const params = new URLSearchParams(location.search);
  const error = params.get("error");
  const errorText = !isUserLoading
    ? error === "session_expired"
      ? "Your session has expired. Please sign in again."
      : error === "state_mismatch"
        ? "Security check failed (state mismatch). Please try signing in again."
        : error === "token_exchange_failed"
          ? "We couldn't complete sign-in. Please try again."
          : error === "login_required"
            ? "Please sign in to continue."
            : error
              ? "An authentication error occurred. Please sign in again."
              : null
    : null;

  const handleSalesforceLogin = async () => {
    clearUrlError();
    clearAuthStorage();
    try {
      const env = readEnv();
      const url = await augmentAuthorizeUrlWithPkce(
        buildAuthorizeUrl(env, { prompt: "none" }),
      );
      window.location.assign(url.toString());
    } catch (e) {
      console.error("Salesforce login error", e);
    }
  };

  return (
    <main
      style={{
        minHeight: "60vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        style={{
          background: "white",
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          padding: 32,
          width: 420,
          maxWidth: "90%",
          boxShadow: "0 10px 20px rgba(0,0,0,0.05)",
          textAlign: "center",
        }}
      >
        <h1 style={{ margin: 0, fontSize: 24 }}>Sign in</h1>

        {getEnv("REACT_APP_AUTO_LOGIN") === "true" && (
          <div style={{ marginTop: 20 }}>
            <Spin tip="Signing you in…" />
          </div>
        )}
        {!(getEnv("REACT_APP_AUTO_LOGIN") === "true") &&
          (isAzureLoginEnabled() && !azureSessionActive ? (
            <>
              <p style={{ color: "#6b7280", marginTop: 16 }}>
                Sign in with your Microsoft work or school account (Azure AD)
                first.
              </p>
              {isUserLoading && !errorText && (
                <div style={{ marginBottom: 16 }}>
                  <Spin />
                </div>
              )}
              <Button
                id="login-btn"
                type="primary"
                size="large"
                onClick={handleMicrosoftLogin}
                disabled={isUserLoading}
                loading={isUserLoading}
                aria-label="Sign in with Microsoft"
                style={{
                  marginTop: 20,
                  border: 0,
                  borderRadius: 800,
                  padding: "10px 24px",
                  cursor: "pointer",
                  fontSize: 16,
                }}
              >
                Continue with Microsoft
              </Button>
            </>
          ) : isSfLoginEnabled() && !sfSessionActive ? (
            <>
              <p style={{ color: "#6b7280", marginTop: 16 }}>
                {isAzureLoginEnabled()
                  ? "Now connect your Salesforce org to continue."
                  : "Sign in with your Salesforce org to continue."}
              </p>
              {errorText && (
                <p style={{ color: "#b91c1c", marginTop: 8, fontSize: 14 }}>
                  {errorText}
                </p>
              )}
              <Button
                id="login-btn"
                type="primary"
                size="large"
                onClick={handleSalesforceLogin}
                aria-label="Continue with Salesforce"
                style={{
                  marginTop: 20,
                  border: 0,
                  borderRadius: 800,
                  padding: "10px 24px",
                  cursor: "pointer",
                  fontSize: 16,
                }}
              >
                Continue with Salesforce
              </Button>
            </>
          ) : (
            <div style={{ marginTop: 20 }}>
              <Spin tip="Signing you in…" />
            </div>
          ))}
      </div>
    </main>
  );
}

export default function Login() {
  const { isUserLoading, isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isUserLoading && isAuthenticated) {
      const from = location.state?.from?.pathname ?? "/";
      navigate(from, { replace: true });
    }
  }, [
    isUserLoading,
    isAuthenticated,
    navigate,
    location.state?.from?.pathname,
  ]);

  useEffect(() => {
    if (!isUserLoading) return;
    clearUrlError();
  }, [isUserLoading, location.pathname]);

  if (isAzureLoginEnabled() && getMsalInstance()) {
    return <LoginAzureFlow />;
  }
  return <LoginSalesforceOnly />;
}

/**
 * Salesforce-only login (no Azure). Used when REACT_APP_AUTH_PROVIDER !== "azure".
 */
function LoginSalesforceOnly() {
  const { login, isUserLoading, isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isUserLoading && isAuthenticated) {
      const from = location.state?.from?.pathname ?? "/";
      navigate(from, { replace: true });
    }
  }, [
    isUserLoading,
    isAuthenticated,
    navigate,
    location.state?.from?.pathname,
  ]);

  const handleSalesforceLogin = useCallback(() => {
    clearUrlError();
    clearAuthStorage();
    login(true);
  }, [login]);

  useEffect(() => {
    const btn = document.getElementById("login-btn");
    btn?.focus();
    if (
      getEnv("REACT_APP_AUTO_LOGIN") === "true" &&
      !isUserLoading &&
      !isAuthenticated
    ) {
      handleSalesforceLogin();
    }
  }, [isUserLoading, isAuthenticated, handleSalesforceLogin]);

  useEffect(() => {
    if (!isUserLoading) return;
    clearUrlError();
  }, [isUserLoading, location.pathname]);

  const params = new URLSearchParams(location.search);
  const error = params.get("error");
  const errorText = !isUserLoading
    ? error === "session_expired"
      ? "Your session has expired. Please sign in again."
      : error === "state_mismatch"
        ? "Security check failed (state mismatch). Please try signing in again."
        : error === "token_exchange_failed"
          ? "We couldn't complete sign-in. Please try again."
          : error === "login_required"
            ? "Please sign in to continue."
            : error
              ? "An authentication error occurred. Please sign in again."
              : null
    : null;

  const loginDisabled = !isSfLoginEnabled();

  return (
    <main
      style={{
        minHeight: "60vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        style={{
          background: "white",
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          padding: 32,
          width: 420,
          maxWidth: "90%",
          boxShadow: "0 10px 20px rgba(0,0,0,0.05)",
          textAlign: "center",
        }}
      >
        {getEnv("REACT_APP_AUTO_LOGIN") === "true" && (
          <p style={{ color: "#6b7280", marginTop: 16 }}>
            Logging in with Salesforce...
          </p>
        )}
        {!(getEnv("REACT_APP_AUTO_LOGIN") === "true") && (
          <>
            <h1 style={{ margin: 0, fontSize: 24 }}>Sign in</h1>
            {loginDisabled ? (
              <p style={{ color: "#6b7280", marginTop: 16 }}>
                No login method is enabled. Enable Salesforce or Azure login in
                configuration.
              </p>
            ) : (
              <>
                <p style={{ color: "#6b7280", marginTop: 16 }}>
                  Sign in with your Salesforce org to continue.
                </p>
                {isUserLoading && !errorText && (
                  <div style={{ marginBottom: 16 }}>
                    <Spin />
                  </div>
                )}
                {errorText && (
                  <p style={{ color: "#b91c1c", marginTop: 8, fontSize: 14 }}>
                    {errorText}
                  </p>
                )}
                <Button
                  id="login-btn"
                  type="primary"
                  size="large"
                  onClick={handleSalesforceLogin}
                  disabled={isUserLoading}
                  loading={isUserLoading}
                  aria-label="Sign in with Salesforce"
                  style={{
                    marginTop: 20,
                    border: 0,
                    borderRadius: 800,
                    padding: "10px 24px",
                    cursor: "pointer",
                    fontSize: 16,
                  }}
                >
                  Continue with Salesforce
                </Button>
              </>
            )}
          </>
        )}
      </div>
    </main>
  );
}
